"use client"

import clsx from "clsx"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { TIconButton } from "../button"
import { TText } from "../display"
import { TLoader } from "../display/TLoader"
import { Slider } from "../shadcn/slider"
import { reportVideoDecodeFailure, useInViewport, useVideoPlaybackSlot } from "./videoPlaybackBudget"

/**
 * @description 미디어 비디오 플레이어. 썸네일, 호버 재생, 전체화면 지원
 * @example
 * ```tsx
 * <TMediaVideo
 *   videoSrc="/video.mp4"
 *   thumbnailSrc="/thumb.jpg"
 *   controls
 *   aspectRatio="16/9"
 * />
 * ```
 */
interface TMediaVideoProps {
	/** 썸네일 이미지 URL */
	thumbnailSrc?: string
	/** 비디오 소스 URL */
	videoSrc?: string
	/** true 시 호버로 재생 시작. 외부에서 호버 상태를 제어할 때 사용 */
	isHover?: boolean
	/**
	 * 반복 재생 여부
	 * @default true
	 */
	loop?: boolean
	/**
	 * 자동 재생 여부
	 * @default false
	 */
	autoplay?: boolean
	/** 재생 컨트롤 UI 표시 */
	controls?: boolean
	/** 컨트롤 UI를 포인터 움직임과 무관하게 항상 표시 */
	alwaysShowControls?: boolean
	/** 컨트롤 영역을 제외한 플레이어 화면 클릭 시 재생/일시정지 토글 */
	clickToTogglePlayback?: boolean
	/** 너비. 숫자(px) 또는 문자열("100%") */
	width?: number | string
	/** 높이. 숫자(px) 또는 문자열("100%") */
	height?: number | string
	/** CSS aspect-ratio 값 (예: "16/9", "1/1") */
	aspectRatio?: string
	/** CORS 설정 */
	crossOrigin?: "anonymous" | "use-credentials"
	/** 추가 CSS 클래스 */
	className?: string
	/** 음소거 상태 (제어 컴포넌트 모드) */
	muted?: boolean
	/** 음소거 상태 변경 콜백 */
	onMutedChange?: (muted: boolean) => void
	/** 전체화면 상태 변경 콜백 */
	onFullscreenChange?: (isFullscreen: boolean) => void
}

interface IVideoState {
	currentTime: number
	duration: number
	paused: boolean
	loading: boolean
	muted: boolean
	audioTracks: number
	fullscreen: boolean
}

// HTMLVideoElement의 audioTracks 속성을 위한 타입 확장
interface ExtendedHTMLVideoElement extends HTMLVideoElement {
	audioTracks?: {
		length: number
	}
	mozHasAudio?: boolean
	webkitAudioDecodedByteCount?: number
}

export const TMediaVideo = React.memo((props: TMediaVideoProps) => {
	const {
		className,
		thumbnailSrc,
		videoSrc,
		isHover,
		loop = true,
		autoplay = false,
		controls,
		alwaysShowControls = false,
		clickToTogglePlayback = false,
		width = "100%",
		height = "100%",
		aspectRatio = "1/1",
		crossOrigin,
		muted: controlledMuted,
		onMutedChange,
		onFullscreenChange
	} = props

	const [internalHover, setInternalHover] = useState<boolean>(autoplay)
	const [loading, setLoading] = useState(true)
	const imgRef = useRef<HTMLImageElement | null>(null)
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)

	const hover = typeof isHover === "boolean" ? isHover : internalHover

	// 카드 루프 모드: Android MediaCodec 디코더 고갈 방지를 위해
	// 뷰포트 교차 + 전역 budget 슬롯을 모두 만족할 때만 src를 부착해 재생한다
	const budgetMode = autoplay && loop && !controls
	const inViewport = useInViewport(containerRef, budgetMode)
	const slotGranted = useVideoPlaybackSlot(budgetMode && inViewport)
	const playbackActive = !budgetMode || (inViewport && slotGranted)

	// useCallback([]) 안정 콜백(attachVideoRef)에서 최신 budgetMode 를 읽기 위한 ref 미러.
	// deps 를 [budgetMode] 로 바꾸면 budget 사용처의 key 교체 시 ref re-attach 부수효과로
	// 의도치 않은 src 분리가 생길 수 있어 ref 미러로 읽는다.
	const budgetModeRef = useRef(budgetMode)
	useEffect(() => {
		budgetModeRef.current = budgetMode
	}, [budgetMode])

	const [videoState, setVideoState] = useState<IVideoState>({
		currentTime: 0,
		duration: 0,
		// paused 는 <video> 의 play/pause 이벤트로 동기화(아래 handlePlay/handlePause) — autoplay 를 낙관 반영(!autoplay)하면
		// 브라우저 정책으로 autoplay 가 거부됐을 때 실제로는 정지인데 pause 아이콘이 보이는 역전이 생긴다. 초기값은 DOM 초기 상태와 동일한 true.
		paused: true,
		loading: true,
		muted: true,
		audioTracks: 0,
		fullscreen: false
	})

	// Use controlled muted if provided, otherwise use internal state
	const muted = controlledMuted ?? videoState.muted

	// Android에서 loop 속성이 동작하지 않는 경우를 대비한 수동 폴백.
	// 비-loop 종료 시엔 paused 동기화 — 일부 브라우저는 ended 에서 pause 이벤트를 따로 내지 않아 아이콘이 재생중으로 남는다.
	const handleEnded = useCallback(() => {
		const video = videoRef.current
		if (!video) return
		if (!loop) {
			setVideoState((prev) => ({ ...prev, paused: true }))
			return
		}
		video.currentTime = 0
		video.play().catch(() => {})
	}, [loop])

	// paused 의 단일 진실 소스 = <video> 이벤트 — 코드 어느 경로가 재생/정지시켜도(자동재생·hover·budget·seek) 아이콘이 실제 상태를 따른다.
	const handlePlay = useCallback(() => setVideoState((prev) => ({ ...prev, paused: false })), [])
	const handlePause = useCallback(() => setVideoState((prev) => ({ ...prev, paused: true })), [])

	const handleImageLoad = useCallback(() => setLoading(false), [])
	const handleVideoLoad = useCallback(async () => {
		setLoading(false)
		const video = videoRef.current as ExtendedHTMLVideoElement
		if (video) {
			// paused 는 autoplay 추정이 아니라 DOM 실측(video.paused) — autoplay 거부 시 play 이벤트가 아예 없어 이벤트 동기화만으론 못 잡는다.
			// controls가 없으면 오디오 감지 불필요 — Android에서 play/pause 조작이 loop 상태를 깨뜨릴 수 있음
			if (!controls) {
				setVideoState((prev) => ({
					...prev,
					currentTime: 0,
					duration: video.duration,
					paused: video.paused,
					loading: false,
					audioTracks: 0,
					fullscreen: false
				}))
				return
			}

			try {
				const hasAudio = await detectVideoAudio(video)
				setVideoState((prev) => ({
					...prev,
					currentTime: 0,
					duration: video.duration,
					paused: video.paused,
					loading: false,
					audioTracks: hasAudio ? 1 : 0,
					fullscreen: false
				}))
			} catch (error) {
				console.error(error)
				setVideoState((prev) => ({
					...prev,
					currentTime: 0,
					duration: video.duration,
					paused: video.paused,
					loading: false,
					audioTracks: 0,
					fullscreen: false
				}))
			}
		}
	}, [controls])

	useEffect(() => {
		if (imgRef.current && imgRef.current.complete) {
			if (imgRef.current.naturalWidth > 0) {
				handleImageLoad()
			}
		}
	}, [])

	// 마운트 시 비디오가 이미 메타데이터 보유(캐시/조기 로드)면 onLoadedMetadata 이벤트를 놓쳤어도 로더 해제 — success 폴백.
	useEffect(() => {
		if (budgetMode) return // budgetMode는 아래 budget effect가 로드/재생 수명주기를 전담
		const video = videoRef.current
		if (video && video.readyState >= 1) setLoading(false)
	}, [])

	useEffect(() => {
		if (budgetMode) return // budgetMode는 아래 budget effect가 로드/재생 수명주기를 전담
		const video = videoRef.current
		if (video) {
			setLoading(true)
			video.load()
			if (autoplay) {
				const playPromise = video.play()
				if (playPromise !== undefined) {
					playPromise.catch((error) => {
						// AbortError는 새로운 로드 요청으로 인한 정상적인 중단이므로 경고하지 않음
						if (error.name !== "AbortError") {
							console.warn("Autoplay prevented:", error)
						}
					})
				}
			}
		}
	}, [videoSrc, budgetMode])

	// 디코더 파이프라인 에러 시 재초기화 재시도 횟수 (활성화될 때마다 리셋)
	const errorRetryCountRef = useRef(0)

	useEffect(() => {
		if (!budgetMode) return
		const video = videoRef.current
		if (!video) return

		if (playbackActive) {
			errorRetryCountRef.current = 0
			setLoading(true)
			video.load()
			video.play().catch((error) => {
				if (error.name !== "AbortError") {
					console.warn("Autoplay prevented:", error)
				}
			})
		} else {
			// pause만으로는 Chromium이 디코더를 반납하지 않음 — src 분리 + load()로 실반납
			video.pause()
			video.removeAttribute("src")
			video.load()
			setLoading(false)
		}
	}, [budgetMode, playbackActive, videoSrc])

	// 엘리먼트가 DOM에서 분리되는 시점(unmount/key 교체)에 src 분리 + load()로 디코더를 즉시 반납한다.
	// 슬롯 반납(useVideoPlaybackSlot cleanup)만으로는 이전 엘리먼트의 MediaCodec 점유가
	// GC까지 잔류해 라우트 전환 직후 budget을 순간 초과할 수 있다 (React 19 ref cleanup)
	const attachVideoRef = useCallback((video: HTMLVideoElement | null) => {
		videoRef.current = video
		if (!video) return undefined
		return () => {
			videoRef.current = null
			video.pause()
			// budgetMode(Android 카드 루프)에서만 디코더 즉시 반납을 위해 src 를 분리한다.
			// 비-budget(채팅·데스크탑·iOS)에서 무조건 분리하면 React 19 dev StrictMode 의
			// 마운트 시 모의 cleanup 이 방금 박힌 DOM src 를 제거하고 React 가 재커밋하지 않아
			// onLoadedMetadata 가 영영 발화하지 않고 로더가 무한 고착된다(dev 무한 스피너).
			if (budgetModeRef.current) {
				video.removeAttribute("src")
				video.load()
			}
		}
	}, [])

	// 로드 실패(CORS/credentials/403/404/코덱) 시 error 만 발화하고 loadedmetadata 는 안 옴 → 로더 해제(loading=true 영구 = 무한 스피너 회귀 방지).
	// 추가로 budgetMode(Android)에선 디코더 할당 실패 시 load()로 재초기화 (최대 2회).
	const handleVideoError = useCallback(() => {
		setLoading(false)
		if (!budgetMode) return
		const video = videoRef.current
		if (!video || !video.getAttribute("src")) return
		// 디코드 실패는 디코더 인스턴스 부족 신호 — budget 상한을 적응적으로 축소
		if (video.error?.code === MediaError.MEDIA_ERR_DECODE) {
			reportVideoDecodeFailure()
		}
		if (errorRetryCountRef.current >= 2) return
		errorRetryCountRef.current += 1
		video.load()
		video.play().catch(() => {})
	}, [budgetMode])

	useEffect(() => {
		if (autoplay) {
			setInternalHover(true)
		}
	}, [autoplay])

	useEffect(() => {
		const video = videoRef.current
		if (!video || loading) return

		if (!autoplay) {
			if (hover) {
				// 로드가 이미 error 로 끝난 엘리먼트(만료 signed URL·CORS 차단 등)에 play() 하면
				// NotSupportedError 로 콘솔만 오염 — 구 동작(침묵 스킵) 유지.
				if (video.error) return
				// readyState 게이트 없이 즉시 play() — 데이터 미도착이면 브라우저가 준비되는 즉시 자동 시작(펜딩 프라미스).
				// 기존 readyState>=2 게이트는 canplay 재시도 리스너가 없어, 콜드캐시 첫 hover 에서
				// loadedmetadata(readyState=1) 시점에 스킵된 뒤 재생 트리거가 영영 없는 dead zone 을 만들었다
				// (hover 를 뗐다 다시 올려야 재생). load() 경합의 AbortError 는 기존대로 무시.
				video.play().catch((err) => {
					if (err.name !== "AbortError") {
						console.warn("video play error:", err)
					}
				})
			} else {
				video.pause()
				video.currentTime = 0
			}
		}
	}, [autoplay, hover, loading])

	const [showControls, setShowControls] = useState(false)
	const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const startHideControlsTimer = useCallback(() => {
		if (alwaysShowControls) return
		if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current)
		hideControlsTimer.current = setTimeout(() => {
			setShowControls(false)
		}, 2000)
	}, [alwaysShowControls])

	const clearHideControlsTimer = useCallback(() => {
		if (hideControlsTimer.current) {
			clearTimeout(hideControlsTimer.current)
			hideControlsTimer.current = null
		}
	}, [])

	const handleMouseMove = useCallback(() => {
		if (typeof isHover !== "boolean") {
			setInternalHover(true)
		}
		if (alwaysShowControls) return
		setShowControls(true)
		startHideControlsTimer()
	}, [isHover, alwaysShowControls, startHideControlsTimer])

	const handleMouseLeave = useCallback(() => {
		if (typeof isHover !== "boolean") {
			setInternalHover(false)
		}
		if (alwaysShowControls) return
		setShowControls(false)
		clearHideControlsTimer()
	}, [isHover, alwaysShowControls, clearHideControlsTimer])

	// 런타임에 always 모드로 전환돼도 기존 자동 숨김 타이머가 뒤늦게 상태를 갱신하지 않도록 정리한다.
	useEffect(() => {
		if (alwaysShowControls) clearHideControlsTimer()
	}, [alwaysShowControls, clearHideControlsTimer])

	const onTimeUpdate = useCallback(() => {
		const video = videoRef.current
		if (!video) return

		// Android에서 ended 이벤트 자체가 발생하지 않는 경우를 대비한 루프 폴백
		// controls 모드(수동 제어)와 탐색(seek) 중에는 실행하지 않음
		if (loop && !controls && !video.seeking && video.duration && video.duration - video.currentTime < 0.3) {
			video.currentTime = 0
			video.play().catch(() => {})
		}

		setVideoState((prev) => ({ ...prev, currentTime: video.currentTime, duration: video.duration }))
	}, [loop, controls])

	const formatDuration = useCallback((duration: number = 0): string => {
		if (isNaN(duration)) return "00:00"
		const minutes = Math.floor(duration / 60)
		const seconds = Math.floor(duration % 60)

		if (seconds === 60) {
			return `${minutes + 1}:00`
		}

		return `${minutes}:${seconds.toString().padStart(2, "0")}`
	}, [])

	// state 반전 대신 DOM(video.paused) 기준 토글 — state 가 어긋나 있어도 첫 클릭이 항상 기대 동작(정지→재생/재생→정지)을 한다.
	// state 갱신은 play/pause 이벤트(handlePlay/handlePause)가 담당, 재생 거부 시에만 명시 복구.
	const togglePlay = useCallback(() => {
		const video = videoRef.current
		if (!video) return
		if (video.paused) {
			video.play().catch(() => {
				setVideoState((prev) => ({ ...prev, paused: true }))
			})
		} else {
			video.pause()
		}
	}, [])

	// 화면 클릭 토글은 opt-in 상세 플레이어 전용. controls 안의 재생/탐색/음소거/전체화면 클릭은
	// 각 컨트롤이 소유하며 여기서 다시 토글하지 않는다. loading 중 클릭도 pending load/autoplay와 경합하지 않게 차단한다.
	const handlePlaybackAreaClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (loading) return
			const target = event.target
			if (target instanceof Element && target.closest(".controls")) return
			togglePlay()
		},
		[loading, togglePlay]
	)

	const [seekValue, setSeekValue] = useState<number | null>(null) // 드래그 중 슬라이더 값
	// 드래그 직전 재생 여부 — pause 이벤트가 paused state 를 true 로 동기화하므로 state 로는 "드래그 전 재생중" 을 알 수 없다.
	const wasPlayingBeforeSeekRef = useRef(false)

	const handleSeekStart = useCallback((value: number[]) => {
		const video = videoRef.current
		if (!video) return
		wasPlayingBeforeSeekRef.current = !video.paused // pause() 호출 전에 DOM 에서 캡처
		video.pause() // 드래그 시작 시 일시정지

		const nextTime = value[0] ?? 0
		setSeekValue(Number.isFinite(nextTime) ? nextTime : 0)
	}, [])

	const handleSeekChange = useCallback((value: number[]) => {
		const video = videoRef.current
		if (!video) return
		const nextTime = value[0] ?? 0
		const finite = Number.isFinite(nextTime)
		// 비유한값(duration 미로딩 시 NaN)은 0으로 정규화해 슬라이더 thumb 깨짐 방지
		setSeekValue(finite ? nextTime : 0) // videoState.currentTime 대신 로컬 값만
		if (finite) video.currentTime = nextTime
	}, [])

	// 드래그 종료 공통 처리 — 로컬 값을 버리고 videoState 로 복귀, 드래그 전 재생중이었을 때만 재개.
	// ref 는 소비 후 리셋하므로 두 번 불려도(pointerup + onValueCommit) 재개는 한 번만 한다.
	const finishSeek = useCallback(() => {
		setSeekValue(null)
		const shouldResume = wasPlayingBeforeSeekRef.current
		wasPlayingBeforeSeekRef.current = false
		if (shouldResume) {
			videoRef.current?.play().catch(() => {})
		}
	}, [])

	const handleSeekEnd = useCallback(
		(value: number[]) => {
			const video = videoRef.current
			if (!video) return
			const nextTime = value[0] ?? 0
			if (Number.isFinite(nextTime)) video.currentTime = nextTime
			finishSeek()
		},
		[finishSeek]
	)

	useEffect(() => {
		const onFullChange = () => {
			const isFullscreen = !!document.fullscreenElement
			setVideoState((prev) => ({ ...prev, fullscreen: isFullscreen }))
			onFullscreenChange?.(isFullscreen)
		}
		document.addEventListener("fullscreenchange", onFullChange)
		return () => document.removeEventListener("fullscreenchange", onFullChange)
	}, [onFullscreenChange])

	const fullscreenRef = useRef(false)
	useEffect(() => {
		fullscreenRef.current = videoState.fullscreen
	}, [videoState.fullscreen])

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (fullscreenRef.current && e.key === "Escape") {
				e.stopPropagation() // 모달로 이벤트 전파 막기
				e.preventDefault()
				setVideoState((prev) => ({ ...prev, fullscreen: false }))
				onFullscreenChange?.(false)
			}
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [onFullscreenChange])

	const handleAudio = useCallback(() => {
		const newMuted = !muted

		// Update internal state only if not controlled
		if (controlledMuted === undefined) {
			setVideoState((prev) => ({ ...prev, muted: newMuted }))
		}

		// Notify parent
		onMutedChange?.(newMuted)
	}, [muted, controlledMuted, onMutedChange])

	const handleFullscreen = useCallback(() => {
		setVideoState((prev) => {
			const newFullscreen = !prev.fullscreen
			onFullscreenChange?.(newFullscreen)
			return { ...prev, fullscreen: newFullscreen }
		})
	}, [onFullscreenChange])

	return (
		<section
			ref={containerRef}
			className={clsx(
				"TMedia video",
				className,
				clickToTogglePlayback && "cursor-pointer [&_.controls]:cursor-default",
				videoState.fullscreen && "fixed! top-0 left-0 w-screen! h-screen! z-2000"
			)}
			style={{ width, height, aspectRatio }}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			onClick={clickToTogglePlayback ? handlePlaybackAreaClick : undefined}
		>
			{loading && !budgetMode && <TLoader size={24} className="loader" />}

			<video
				key={videoSrc}
				ref={attachVideoRef}
				src={playbackActive ? videoSrc : undefined}
				autoPlay={autoplay}
				loop={loop}
				muted={muted}
				playsInline
				// poster 는 항상 fallback 으로 둔다 — autoplay 비-budget(예: 결과 상세 모달/사이드바)에서 자동재생이 정책 차단/지연/디코드 실패로
				// 첫 프레임을 못 그리면 poster 가 없을 경우 영상 영역이 통째로 빈 화면이 되던 회귀 방지. poster 는 프레임이 생기면 자동 대체되므로 무해.
				poster={thumbnailSrc}
				crossOrigin={crossOrigin}
				preload="metadata"
				onLoadedMetadata={handleVideoLoad}
				onError={handleVideoError}
				onTimeUpdate={onTimeUpdate}
				onEnded={handleEnded}
				onPlay={handlePlay}
				onPause={handlePause}
				className={clsx({ invisible: loading && !budgetMode })}
				disablePictureInPicture
				disableRemotePlayback
			/>

			{controls && (alwaysShowControls || internalHover) && (
				<div
					className={clsx(
						"controls absolute w-full p-2 pt-3 bottom-0 h-15 flex items-end z-50 transition-opacity duration-300",
						"bg-linear-to-t from-black/70 to-transparent",
						alwaysShowControls || showControls ? "opacity-100" : "opacity-0 pointer-events-none"
					)}
					onClick={clickToTogglePlayback ? (event) => event.stopPropagation() : undefined}
					onMouseEnter={clearHideControlsTimer} // controls 위에 올리면 타이머 멈춤
					onMouseLeave={startHideControlsTimer} // controls에서 나가면 다시 시작
					onMouseMove={(e) => {
						e.stopPropagation() // section으로 이벤트 전달 방지
					}}
				>
					<div className="w-full flex items-center space-x-2 [&_.TIconButton]:shrink-0">
						<TIconButton name={videoState.paused ? "play" : "pause"} size={"xsmall"} color="tertiary" onClick={togglePlay} />
						<Slider
							min={0}
							step={0.01}
							max={Number.isFinite(videoState.duration) ? videoState.duration : 0}
							value={[seekValue ?? videoState.currentTime]}
							onValueChange={handleSeekChange}
							onValueCommit={handleSeekEnd}
							onPointerDown={(e) => {
								e.stopPropagation()
								handleSeekStart([seekValue ?? videoState.currentTime])
							}}
							// Radix 는 값이 안 바뀐 클릭(현재 위치 그대로 누르기)에 onValueCommit 을 부르지 않는다. 그러면 seekValue 가
							// 영영 남아 막대가 멈추고 pause 만 남는다 — 포인터를 떼는 순간 커밋과 무관하게 드래그를 끝낸다.
							onPointerUp={finishSeek}
							onPointerCancel={finishSeek}
						/>
						<TText variant="caption" bold className="min-w-[64px] text-right">
							{formatDuration(videoState.currentTime)}/{formatDuration(videoState.duration)}
						</TText>
						<TIconButton name={muted ? "mute" : "soundOn"} size={"xsmall"} color="tertiary" onClick={handleAudio} />
						<TIconButton name={videoState.fullscreen ? "fullscreenExit" : "fullscreen"} size={"xsmall"} color="tertiary" onClick={handleFullscreen} />
					</div>
				</div>
			)}
		</section>
	)
})

TMediaVideo.displayName = "TMediaVideo"

const detectVideoAudio = (video: ExtendedHTMLVideoElement): Promise<boolean> => {
	return new Promise((resolve) => {
		const detectMethods = () => {
			const hasStandardAudioTracks = video.audioTracks && video.audioTracks.length > 0
			const hasMozAudio = video.mozHasAudio === true
			const hasWebkitAudio = Boolean(video.webkitAudioDecodedByteCount && video.webkitAudioDecodedByteCount > 0)

			return hasStandardAudioTracks || hasMozAudio || hasWebkitAudio
		}

		let hasAudio = detectMethods()

		if (hasAudio) {
			resolve(true)
			return
		}

		setTimeout(() => {
			hasAudio = detectMethods()

			if (hasAudio) {
				resolve(true)
			} else {
				const originalTime = video.currentTime
				const originalMuted = video.muted

				video.muted = true
				video.currentTime = 0

				const checkAfterPlay = () => {
					setTimeout(() => {
						const finalCheck = detectMethods()
						video.currentTime = originalTime
						video.muted = originalMuted
						resolve(finalCheck)
					}, 200)
				}

				if (video.paused) {
					video
						.play()
						.then(() => {
							video.pause()
							checkAfterPlay()
						})
						.catch(() => {
							resolve(false)
						})
				} else {
					checkAfterPlay()
				}
			}
		}, 300)
	})
}
